// @ts-strict-ignore
import type { ChartType, TooltipItem, TooltipOptions } from "chart.js";
import dayjs from "dayjs";

import type { ChartConfiguration } from "components/ui/Chart/chart.types";
import { theme } from "styles/theme";
import { type FormattedYearWeek, type TimeScale, dateToFormattedYearWeek } from "utils/date.utils";

export type TooltipPluginConfiguration = ChartConfiguration<any>["options"]["plugins"]["tooltip"];

export const tooltipConfiguration: TooltipPluginConfiguration = {
    intersect: false,
    mode: "nearest",
    axis: "x",
    padding: {
        x: 12,
        y: 8,
    },
    cornerRadius: 4,
    backgroundColor: theme.color.white,
    titleColor: theme.color.textGray,
    bodyColor: theme.color.textGray,
    bodySpacing: 4,
    titleMarginBottom: 8,
    boxPadding: 4,
    usePointStyle: true,
    // Disable tooltip if dataset does not have a label
    filter: (tooltipItem) => tooltipItem.dataset.label !== undefined,
};

type ChartTypeOrUnknown = ChartType | unknown;

type TypedTooltipItem<TRawDataShape, TOptionalChartType extends ChartTypeOrUnknown> = Omit<
    // 'any' type required due to chart.js 'TooltipItem' type constraint instead of unknown
    TooltipItem<TOptionalChartType extends ChartType ? TOptionalChartType : any>,
    "raw"
> &
    Record<"raw", TRawDataShape>;

type CommonTooltipConfigurationOptions<TRawDataShape, TOptionalChartType extends ChartTypeOrUnknown = unknown> = {
    intersect?: boolean;

    // Callbacks for tooltip customizations (https://www.chartjs.org/docs/latest/configuration/tooltip.html#tooltip-callbacks)
    getTitle?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
    getBeforeBody?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
    getLabel?: (tooltipItem: TypedTooltipItem<TRawDataShape, TOptionalChartType>) => string | string[] | void;
    getAfterLabel?: (tooltipItem: TypedTooltipItem<TRawDataShape, TOptionalChartType>) => string | string[] | void;
    getAfterBody?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
};

export type CreateTooltipConfigurationOptions<TRawDataShape, TOptionalChartType extends ChartTypeOrUnknown = unknown> =
    | (CommonTooltipConfigurationOptions<TRawDataShape, TOptionalChartType> & {
          reverseLabelOrder?: boolean;
          getItemSort?: never;
      })
    | (CommonTooltipConfigurationOptions<TRawDataShape, TOptionalChartType> & {
          reverseLabelOrder?: never;
          getItemSort?: TooltipOptions["itemSort"];
      });

// export type CreateTooltipConfigurationOptions<
//     TRawDataShape,
//     TOptionalChartType extends ChartTypeOrUnknown = unknown,
// > = {
//     intersect?: boolean;
//     reverseLabelOrder?: boolean;

//     // Callbacks for tooltip customizations (https://www.chartjs.org/docs/latest/configuration/tooltip.html#tooltip-callbacks)
//     getTitle?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
//     getBeforeBody?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
//     getLabel?: (tooltipItem: TypedTooltipItem<TRawDataShape, TOptionalChartType>) => string | string[] | void;
//     getAfterLabel?: (tooltipItem: TypedTooltipItem<TRawDataShape, TOptionalChartType>) => string | string[] | void;
//     getAfterBody?: (tooltipItems?: TypedTooltipItem<TRawDataShape, TOptionalChartType>[]) => string | string[] | void;
//     getItemSort?: TooltipOptions["itemSort"];
// };

export const createTooltipConfiguration = <TRawDataShape, TOptionalChartType extends ChartTypeOrUnknown = unknown>(
    options?: CreateTooltipConfigurationOptions<TRawDataShape, TOptionalChartType>
): TooltipPluginConfiguration => {
    const {
        intersect,
        reverseLabelOrder,
        getTitle,
        getLabel,
        getAfterLabel,
        getBeforeBody,
        getAfterBody,
        getItemSort,
    } = options ?? {};

    let itemSort: TooltipOptions["itemSort"] | undefined;
    if (getItemSort) {
        itemSort = getItemSort;
    } else if (reverseLabelOrder) {
        itemSort = (a, b) => {
            return b.datasetIndex - a.datasetIndex;
        };
    }

    return {
        ...tooltipConfiguration,
        intersect: !!intersect,
        itemSort,
        callbacks: {
            title: getTitle,
            beforeBody: getBeforeBody,
            label: getLabel,
            afterLabel: getAfterLabel,
            afterBody: getAfterBody,
            labelPointStyle: () => {
                return {
                    pointStyle: "rectRounded",
                };
            },
        },
    };
};

export const addTooltipLabelSuffix = (tooltipItem: TooltipItem<any>, suffix?: string | null): string => {
    return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}${suffix ?? ""}`;
};

export const sortItemsByValue = (a, b) => {
    return Number(b.raw) - Number(a.raw);
};

type GetKPIDateFormatProps = {
    date: string | number;
    timeScale: TimeScale;
    format: keyof FormattedYearWeek;
    useISOWeek?: boolean;
};
export const getDateInKPIFormat = (props: GetKPIDateFormatProps) => {
    const { date, timeScale, format, useISOWeek } = props;

    if (timeScale === "month") {
        return dayjs(date).format(format === "short" ? "MMM YYYY" : "MMMM YYYY");
    }

    const formatted = dateToFormattedYearWeek({ date, useISOWeek });

    return formatted[format];
};
